import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import Checkbox from "@mui/material/Checkbox"
import Collapse from "@mui/material/Collapse"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Skeleton from "@mui/material/Skeleton"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { format as formatDate, set as updateDate } from "date-fns"
import * as fileDownload from "js-file-download"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import TabData from "../../../common/TabData"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import ConditionalLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import TabContent from "../../../common/components/TabContent"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../common/components/tables/cells"
import {
  formatTableDate,
  getTableSearchParams,
  usePaginationState,
  useTableSearchTextState,
  useTableSort,
} from "../../../common/components/tables/utils"
import SegmentTracker from "../../../common/tracking"
import {
  formatCurrency,
  trimLongString,
  useAutoNavigateSearchParamState,
  useIsMobile,
} from "../../../common/utils"
import FiltersDialog from "../../../smartsetter-utils/filters-dialog"
import { sanitizeFilters } from "../../../smartsetter-utils/filters-dialog/filterSchema"
import { useDialogState } from "../../../smartsetter-utils/filters-dialog/utils"
import { canUsePowerDialer } from "../../users/accountRole"
import { isActiveSubscriptionStatus } from "../../users/billing"
import {
  selectProfile,
  selectProfileLoaded,
  selectSubscription,
} from "../../users/reducer"
import AssignToPodButton from "../calling/AssignToPodButton"
import StartPowerDialerButton from "../calling/StartPowerDialerButton"
import { POWER_DIALER_SOURCES } from "../calling/powerDialer"
import ConfirmArchiveCandidateDialog from "../candidate-details/ConfirmArchiveCandidateDialog"
import ApplyFiltersButton from "../components/ApplyFiltersButton"
import BorderedGrayChip from "../components/BorderedGrayChip"
import BulkSendEmailCampaignDialogCombo, {
  useBulkSendEmailCampaignDialogComboState,
} from "../components/BulkSendEmailCampaignDialogCombo"
import {
  CandidateActionDialogs,
  useCandidateActions,
} from "../components/CandidateActionController"
import CandidatePriceTagChip from "../components/CandidatePriceTagChip"
import ConfirmConnectEmailDialog from "../components/ConfirmConnectEmailDialog"
import CreateEventDialog, {
  useCreateEventDialogState,
} from "../components/CreateMeetingScheduleDialog"
import GreenCircleChip, {
  GrayCircleIcon,
  GreenCircleIcon,
  PinkCircleIcon,
} from "../components/GreenCircleChip"
import ImportCandidatesDialog from "../components/ImportCandidatesDialog"
import LikelihoodToMoveHeaderTableCell from "../components/LikelihoodToMoveHeaderTableCell"
import LikelihoodToMoveTableCell from "../components/LikelihoodToMoveTableCell"
import NameWithContactActionsCell, {
  phoneDisplay,
  stickyCheckboxCellSx,
  stickyNameCellSx,
} from "../components/NameWithContactActionsCell"
import PageContentContainer from "../components/PageContentContainer"
import SavedFilterTabs, {
  SAVED_FILTER_PAGES,
  useSavedFilterTabsState,
} from "../components/SavedFilterTabs"
import SectionSeparator from "../components/SectionSeparator"
import SelectMultipleTableCell, {
  useSelectMultipleTableCellState,
} from "../components/SelectMultipleTableCell"
import {
  SendingEmailTrialFinishedDialogData,
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "../components/TrialFinishedRoadblockDialog"
import UpdateCreateCandidateDialog from "../components/UpdateCreateCandidateDialog"
import UpdateCreateCandidateFeedbackDialog, {
  useUpdateCreateCandidateFeedbackDialogState,
} from "../components/UpdateCreateCandidateFeedbackDialog"
import {
  DNC_SELECT_TOOLTIP,
  allSelectableChecked,
  dncRowSx,
  isDncLead,
  toggleSelectableRows,
} from "../components/dncLeads"
import { useCandidateImportJobPolling } from "../components/useCandidateImportJobPolling"
import {
  retrieveCRMSettings,
  retrieveNotifications,
  selectCRMSettings,
} from "../reducer"
import { isCheckboxClickEvent } from "../utils"
import CandidatesCalendar, {
  CalendarControls,
  EVENT_NAME_ALL,
  TIME_SLICE_MONTH,
} from "./Calendar"
import CandidatePartialDetailsMobileDialog from "./CandidatePartialDetailsMobileDialog"
import CandidateUsageProgressCard from "./CandidateUsageProgressCard"
import ConfirmArchiveCandidatesDialog from "./ConfirmArchiveCandidatesDialog"
import { FIELD_NAME_TO_COMPONENT_MAP, FILTER_FIELDS } from "./FilterFields"
import { RatingEditor } from "./RatingStars"
import { getCandidateTypeLabel } from "./candidateType"
import {
  CANDIDATES_RESTORE_LIST_CONTEXT_STATE_KEY,
  buildCandidatesListContext,
  navigateToCandidateDetails,
  persistCandidatesListNavigationState,
} from "./navigation"
import { useCandidatesListNavigationState } from "./useCandidatesListNavigationState"

export default function Candidates() {
  const LIST_VIEW_TAB_INDEX = 0
  const CALENDAR_VIEW_TAB_INDEX = 1
  const [activeTabIndex, setActiveTabIndex] = useAutoNavigateSearchParamState(
    "tab",
    LIST_VIEW_TAB_INDEX
  )
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [isCalendarLoading, setIsCalendarLoading] = useState(false)
  const [tableRows, setTableRows] = useState([])
  const [stats, setStats] = useState({
    total_annual_value: 0,
  })
  const {
    totalResultsSize,
    setTotalResultsSize,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
  } = usePaginationState()
  const {
    serverSort,
    sortColumn,
    sortDirection,
    onSortColumnChanged,
    setSort,
  } = useTableSort("stamp", "desc")
  const [tableSearchQuery, setTableSearchQuery, debouncedTableSearchQuery] =
    useTableSearchTextState("q", setPageIndex)
  const {
    filters,
    setFilters,
    debouncedFilters,
    isFiltersDialogOpen,
    openFiltersDialog,
    closeFiltersDialog,
    handleFiltersUpdated,
    clearFilters,
  } = useDialogState()
  const savedViews = useSavedFilterTabsState(
    SAVED_FILTER_PAGES.candidates,
    debouncedFilters,
    setFilters,
    setPageIndex,
    {
      filters,
      sortColumn,
      sortDirection,
      setSort,
      defaultSortColumn: "stamp",
      defaultSortDirection: "desc",
    }
  )
  const { savedFiltersReady } = savedViews
  // The dialog's own row keys (_id, _complete) never reach the API.
  const apiFilters = useMemo(
    () => sanitizeFilters(debouncedFilters),
    [debouncedFilters]
  )
  const [createCandidateDialogOpen, setCreateCandidateDialogOpen] =
    useState(false)
  const [importCandidatesDialogOpen, setImportCandidatesDialogOpen] =
    useState(false)
  const {
    createEventDialogOpen,
    setCreateEventDialogOpen,
    openCreateEventDialog,
  } = useCreateEventDialogState()
  const [actionMenuAnchorElem, setActionMenuAnchorElem] = useState(null)
  const [
    candidatePartialDetailsMobileDialogOpen,
    setCandidatePartialDetailsMobileOpen,
  ] = useState(null)
  const [candidateToArchive, setCandidateToArchive] = useState(null)
  const [isArchivingCandidate, setIsArchivingCandidate] = useState(false)
  const [
    confirmArchiveCandidatesDialogOpen,
    setConfirmArchiveCandidatesDialogOpen,
  ] = useState(false)
  const [isArchivingCandidates, setIsArchivingCandidates] = useState(false)
  const [selectedEventName, setSelectedEventName] = useState(EVENT_NAME_ALL)
  const [selectedTimeSlice, setSelectedTimeSlice] = useState(TIME_SLICE_MONTH)
  const [currentCalendarDate, setCurrentCalendarDate] = useState(
    /* first day of the current month */ updateDate(new Date(), { date: 1 })
  )

  const { checkedIDS, setCheckedIDS, clearSelection, checkedIDsArray } =
    useSelectMultipleTableCellState()
  const {
    bulkSendEmailCampaignDialogOpen,
    closeSnackbar,
    confirmConnectEmailDialogOpen: bulkConfirmConnectEmailDialogOpen,
    emailAuthorized: bulkEmailAuthorized,
    handleBulkSendEmailCampaignDialogClosed,
    handleConfirmConnectGmailDialogClosed:
      handleBulkConfirmConnectGmailDialogClosed,
    handleSendEmailButtonClicked,
    openConfirmConnectEmailDialog: openBulkConfirmConnectEmailDialog,
    openSnackbar,
    isSendingBulkCampaign,
    isSnackbarOpen,
    setIsSendingBulkCampaign,
    snackbarMessage,
    snackbarSeverity,
  } = useBulkSendEmailCampaignDialogComboState(clearSelection)

  const crmSettings = useSelector(selectCRMSettings)
  const dispatch = useDispatch()
  const subscription = useSelector(selectSubscription)
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const hasPaidPlan = isActiveSubscriptionStatus(subscription?.status)
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()

  const candidateActions = useCandidateActions(openSnackbar)

  const {
    candidateReceivingFeedback,
    updateCreateCandidateFeedbackDialogOpen,
    openUpdateCreateCandidateFeedbackDialog,
    closeUpdateCreateCandidateFeedbackDialog,
  } = useUpdateCreateCandidateFeedbackDialogState((payload) => {
    const { timeline_event: _timelineEvent, ...feedback } = payload
    setTableRows(
      tableRows.map((candidate) =>
        candidate.id === candidateReceivingFeedback.id
          ? { ...candidate, feedback }
          : candidate
      )
    )
  })
  const axios = useAuthorizedAxiosClient()
  const queryCandidatesRef = useRef(() => {})
  const candidatesQueryGenerationRef = useRef(0)

  const handleImportJobComplete = useCallback(
    (data) => {
      queryCandidatesRef.current()
      dispatch(retrieveNotifications())
      if (data.message && typeof data.message === "string") {
        openSnackbar(data.message)
      } else if (data.status === "failed") {
        openSnackbar("Candidate import failed")
      } else {
        openSnackbar(
          `${data.created_count ?? 0} candidates imported successfully`
        )
      }
    },
    [dispatch, openSnackbar]
  )

  const { startPolling: startImportJobPolling } = useCandidateImportJobPolling(
    handleImportJobComplete
  )

  const DNC_FILE_INPUT_ID = "file-select-dnc"

  const navigate = useNavigate()
  const location = useLocation()
  const { buildListNavigationStateForRow } = useCandidatesListNavigationState({
    debouncedFilters,
    debouncedTableSearchQuery,
    serverSort,
    pageIndex,
    pageSize,
    tableRows,
    totalResultsSize,
  })
  const isMobile = useIsMobile()

  function handleImportCandidatesMenuItemClicked() {
    closeActionMenu()
    runOnlyIfSubscribed(() => {
      setImportCandidatesDialogOpen(true)
    }, new TrialFinishedRoadblockDialogData("Importing Leads", "Subscribe to import leads using CSV"))
  }

  function handleDownloadImportTemplateMenuItemClicked() {
    closeActionMenu()
    setIsTableLoading(true)
    axios
      .get("/clients/api/candidates/export-csv-sample/", {
        responseType: "blob",
      })
      .then(({ data }) => fileDownload(data, "candidates-import-template.csv"))
      .finally(() => setIsTableLoading(false))
  }

  function handleExportCSVMenuItemClicked() {
    closeActionMenu()
    runOnlyIfSubscribed(() => {
      setIsTableLoading(true)
      axios
        .post(
          "/clients/api/candidates/export-csv/",
          { filters: apiFilters },
          { params: { q: debouncedTableSearchQuery }, responseType: "blob" }
        )
        .then(({ data }) =>
          fileDownload(
            data,
            `candidates-${formatDate(new Date(), "dd-MM-Y")}.csv`
          )
        )
        .finally(() => setIsTableLoading(false))
    }, new TrialFinishedRoadblockDialogData("Exporting Candidates", "Subscribe to export your candidates as CSV"))
  }

  function handleCandidatesImportQueued(data) {
    openSnackbar(
      data.message ||
        "Candidate import started. We'll notify you when it's complete."
    )
    startImportJobPolling(data.job_id)
  }

  function handleDNCRosterFileChanged(e) {
    setIsTableLoading(true)
    const formData = new FormData()
    formData.append("file", document.getElementById(DNC_FILE_INPUT_ID).files[0])
    axios
      .post("/clients/api/upload-dnc/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(() => openSnackbar("DNC roster file uploaded successfully"))
      .finally(() => setIsTableLoading(false))
  }

  function sendBulkCampaign(selectedCampaign) {
    setIsSendingBulkCampaign(true)
    axios
      .post(`/workflows/api/email/send-campaign/${selectedCampaign.id}/bulk/`, {
        candidate_ids: checkedIDsArray,
      })
      .then(() => handleBulkSendEmailCampaignDialogClosed(true))
      .finally(() => setIsSendingBulkCampaign(false))
  }

  function handleSelectAllCandidatesCheckboxCheckChanged(e) {
    // DNC candidates are never checked: the Power Dialer won't call them.
    setCheckedIDS(toggleSelectableRows(checkedIDS, tableRows, e.target.checked))
  }

  function handleRowClickedWrapper(rowData) {
    return function (e) {
      if (isCheckboxClickEvent(e)) {
        return
      }
      const navigationState = buildListNavigationStateForRow(rowData)
      if (!navigationState) {
        navigate(`/candidates/${rowData.id}`)
        return
      }
      if (isMobile) {
        setCandidatePartialDetailsMobileOpen({
          candidate: rowData,
          listNavigationState: navigationState,
        })
      } else {
        navigateToCandidateDetails(
          navigate,
          navigationState.candidatesListSearch,
          navigationState.listContext,
          rowData.id
        )
      }
    }
  }

  function handleConfirmArchiveCandidateFromList(confirmed) {
    if (!confirmed || !candidateToArchive) {
      setCandidateToArchive(null)
      return
    }
    const row = candidateToArchive
    setIsArchivingCandidate(true)
    axios
      .delete(`/crm/api/candidates/${row.id}/`)
      .then(() => {
        setTableRows((prev) => prev.filter((r) => r.id !== row.id))
        setTotalResultsSize((n) => Math.max(0, n - 1))
        setCheckedIDS((prev) => {
          const next = new Set(prev)
          next.delete(row.id)
          return next
        })
        openSnackbar("Candidate removed from your portal")
      })
      .catch(() => openSnackbar("Could not remove candidate. Try again."))
      .finally(() => {
        setIsArchivingCandidate(false)
        setCandidateToArchive(null)
      })
  }

  function handleRatingChangedWrapper(candidate) {
    return (e, newValue) => {
      e.stopPropagation()
      setIsTableLoading(true)
      setTableRows(
        tableRows.map((row) =>
          row.id === candidate.id ? { ...candidate, rating: newValue } : row
        )
      )
      axios
        .put(`/clients/api/candidates/${candidate.id}/rating/`, {
          rating: newValue,
        })
        .then(() => {
          openSnackbar("Rating updated successfully")
        })
        .finally(() => setIsTableLoading(false))
    }
  }

  function handleCreateCandidateDialogClosed(createdCandidated) {
    setCreateCandidateDialogOpen(false)
    if (createdCandidated) {
      navigate(`${createdCandidated.id}`)
    }
  }

  function handleCreateEventDialogClosed(meeting) {
    setCreateEventDialogOpen(false)
    if (meeting) {
      openSnackbar("Event has been created successfully")
    }
  }

  function handleUploadDNCRosterButtonClicked() {
    document.getElementById(DNC_FILE_INPUT_ID).click()
  }

  function handleUploadDNCRosterMenuItemClicked() {
    handleUploadDNCRosterButtonClicked()
    closeActionMenu()
  }

  function handleAddCandidateMenuItemClicked() {
    runOnlyIfSubscribed(
      () => setCreateCandidateDialogOpen(true),
      new TrialFinishedRoadblockDialogData(
        "Adding Candidates",
        "Subscribe to add candidates and communicate with them"
      )
    )
    closeActionMenu()
  }

  function handleAddEventMenuItemClicked() {
    closeActionMenu()
    if (bulkEmailAuthorized) {
      openCreateEventDialog()
    } else {
      openBulkConfirmConnectEmailDialog()
    }
  }

  function handleDeleteCandidatesMenuItemClicked() {
    closeActionMenu()
    if (checkedIDS.size === 0) {
      return
    }
    setConfirmArchiveCandidatesDialogOpen(true)
  }

  function handleConfirmArchiveCandidatesDialogClosed(confirmed) {
    if (!confirmed) {
      setConfirmArchiveCandidatesDialogOpen(false)
      return
    }

    const selectedCandidateIds = checkedIDsArray
    const selectedCandidateIdsSet = new Set(selectedCandidateIds)
    const selectedCount = selectedCandidateIds.length
    setIsArchivingCandidates(true)
    axios
      .delete("/crm/api/candidates/bulk/archive/", {
        data: { candidate_ids: selectedCandidateIds },
      })
      .then(() => {
        setTableRows((prev) =>
          prev.filter((row) => !selectedCandidateIdsSet.has(row.id))
        )
        setTotalResultsSize((n) => Math.max(0, n - selectedCount))
        clearSelection()
        openSnackbar(`${selectedCount} candidates removed from your portal`)
      })
      .catch(() =>
        openSnackbar("Could not remove selected candidates. Try again.")
      )
      .finally(() => {
        setIsArchivingCandidates(false)
        setConfirmArchiveCandidatesDialogOpen(false)
      })
  }

  function handleSendEmailMenuItemClicked() {
    closeActionMenu()
    runOnlyIfSubscribed(
      () => handleSendEmailButtonClicked(),
      SendingEmailTrialFinishedDialogData
    )
  }

  function closeActionMenu() {
    setActionMenuAnchorElem(null)
  }

  function queryCandidates() {
    const generation = ++candidatesQueryGenerationRef.current
    setIsTableLoading(true)
    axios
      .post(
        "/clients/api/candidates/",
        {
          filters: apiFilters,
        },
        {
          params: getTableSearchParams(
            debouncedTableSearchQuery,
            pageIndex,
            pageSize,
            serverSort
          ),
        }
      )
      .then(({ data }) => {
        if (generation !== candidatesQueryGenerationRef.current) {
          return
        }
        setTableRows(data.results)
        setTotalResultsSize(data.count)
        setStats(data.stats)
      })
      .finally(() => {
        if (generation === candidatesQueryGenerationRef.current) {
          setIsTableLoading(false)
        }
      })
  }

  queryCandidatesRef.current = queryCandidates

  const isListQueryPending = !profileLoaded || !savedFiltersReady
  const showTableLoading = isTableLoading || isListQueryPending

  useEffect(
    () => {
      if (!profileLoaded || !savedFiltersReady) {
        return
      }
      queryCandidates()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      profileLoaded,
      savedFiltersReady,
      axios,
      pageIndex,
      pageSize,
      serverSort,
      debouncedTableSearchQuery,
      debouncedFilters,
    ]
  )

  useEffect(
    () => {
      const restoreContext =
        location.state?.[CANDIDATES_RESTORE_LIST_CONTEXT_STATE_KEY]
      if (!restoreContext?.filters?.length) {
        return
      }

      const savedViewTabIndex = Number(
        new URLSearchParams(location.search).get("svdf") || 0
      )
      if (savedViewTabIndex === 0) {
        setFilters(restoreContext.filters)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.state, location.search]
  )

  useEffect(
    () => {
      persistCandidatesListNavigationState({
        candidatesListSearch: location.search,
        listContext: buildCandidatesListContext({
          filters: apiFilters,
          q: debouncedTableSearchQuery,
          ordering: serverSort,
          pageIndex,
          pageSize,
          rowIndex: 0,
          pageCandidateIds: tableRows.map((row) => Number(row.id)),
          totalCount: totalResultsSize,
        }),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      location.search,
      debouncedFilters,
      debouncedTableSearchQuery,
      serverSort,
      pageIndex,
      pageSize,
      tableRows,
      totalResultsSize,
    ]
  )

  return (
    <Column>
      <PageTopSection title="Candidates">
        <RowButColumnOnMobile
          sx={{
            alignItems: { xs: "flex-end", sm: "center" },
            rowGap: 1,
            columnGap: 2,
          }}
        >
          <input
            id={DNC_FILE_INPUT_ID}
            type="file"
            accept=".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv,  application/x-csv, text/comma-separated-values, text/x-comma-separated-values, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleDNCRosterFileChanged}
            style={{ display: "none" }}
          />
          {hasPaidPlan && canUsePowerDialer(profile) ? (
            <>
              <StartPowerDialerButton
                source={POWER_DIALER_SOURCES.candidates}
                filters={apiFilters}
                ordering={serverSort}
                selectedIds={checkedIDsArray}
              />
              <AssignToPodButton
                source={POWER_DIALER_SOURCES.candidates}
                filters={apiFilters}
                ordering={serverSort}
                selectedIds={checkedIDsArray}
              />
            </>
          ) : null}
          <IconButton onClick={(e) => setActionMenuAnchorElem(e.currentTarget)}>
            <MoreVertOutlinedIcon />
          </IconButton>
          <Menu
            open={Boolean(actionMenuAnchorElem)}
            anchorEl={actionMenuAnchorElem}
            onClose={closeActionMenu}
          >
            <MenuItem onClick={handleImportCandidatesMenuItemClicked}>
              <ListItemIcon>
                <PersonAddOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Import Leads</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportCSVMenuItemClicked}>
              <ListItemIcon>
                <FileDownloadOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Export CSV</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleUploadDNCRosterMenuItemClicked}>
              <ListItemIcon>
                <CloudUploadOutlined />
              </ListItemIcon>
              <ListItemText>Upload DNC Roster</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddCandidateMenuItemClicked}>
              <ListItemIcon>
                <AddCircleOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Add Candidate</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddEventMenuItemClicked}>
              <ListItemIcon>
                <CalendarTodayOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Add Event</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleSendEmailMenuItemClicked}
              disabled={checkedIDS.size === 0}
            >
              <ListItemIcon>
                <EmailOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Send email campaign</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleDeleteCandidatesMenuItemClicked}
              disabled={checkedIDS.size === 0 || isArchivingCandidates}
            >
              <ListItemIcon>
                <DeleteOutlinedIcon />
              </ListItemIcon>
              <ListItemText>Delete selected candidates</ListItemText>
            </MenuItem>
          </Menu>
          <ConfirmConnectEmailDialog
            open={bulkConfirmConnectEmailDialogOpen}
            onClose={handleBulkConfirmConnectGmailDialogClosed}
          />
          {isMobile ? (
            <TabSwitcher
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
            />
          ) : null}
        </RowButColumnOnMobile>
      </PageTopSection>
      <PageContentContainer>
        <Collapse
          in={!subscription.isActive && subscription.usedCandidateCount > 0}
        >
          <CandidateUsageProgressCard />
        </Collapse>
        <RowButColumnOnMobile
          sx={{
            alignItems: "center",
            gap: 2,
          }}
        >
          <TabContent value={activeTabIndex} index={LIST_VIEW_TAB_INDEX}>
            <BorderedGrayChip
              label={`${formatCurrency(
                stats.total_annual_value
              )} total annual value`}
              sx={{
                order: { xs: 3, sm: 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
          </TabContent>
          <TabContent value={activeTabIndex} index={LIST_VIEW_TAB_INDEX}>
            <RowButColumnOnMobile
              sx={{
                flexDirection: {
                  xs: "column-reverse",
                  sm: "row",
                  width: "100%",
                },
                justifyContent: "flex-end",
                gap: 2,
                flexBasis: "100%",
              }}
            >
              <TextField
                placeholder="Search"
                size="small"
                sx={{ minWidth: "5em" }}
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                fullWidth={isMobile}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <BulkSendEmailCampaignDialogCombo
                bulkSendEmailCampaignDialogOpen={
                  bulkSendEmailCampaignDialogOpen
                }
                handleBulkSendEmailCampaignDialogClosed={
                  handleBulkSendEmailCampaignDialogClosed
                }
                onSendEmailCampaignButtonClicked={sendBulkCampaign}
                confirmConnectGmailDialogOpen={
                  bulkConfirmConnectEmailDialogOpen
                }
                handleConfirmConnectGmailDialogClosed={
                  handleBulkConfirmConnectGmailDialogClosed
                }
                candidateCount={checkedIDS.size}
                isSending={isSendingBulkCampaign}
              />
              <ApplyFiltersButton
                openFiltersDialog={openFiltersDialog}
                filters={filters}
              />
              <FiltersDialog
                open={isFiltersDialogOpen}
                onClosed={closeFiltersDialog}
                parentFilters={filters}
                onFiltersUpdated={handleFiltersUpdated}
                onFiltersCleared={clearFilters}
                filterTypes={FILTER_FIELDS}
                fieldNameToComponentMap={FIELD_NAME_TO_COMPONENT_MAP}
              />
            </RowButColumnOnMobile>
          </TabContent>
          <UpdateCreateCandidateDialog
            open={createCandidateDialogOpen}
            onClose={handleCreateCandidateDialogClosed}
          />
          <ImportCandidatesDialog
            open={importCandidatesDialogOpen}
            onClose={() => setImportCandidatesDialogOpen(false)}
            onImportQueued={handleCandidatesImportQueued}
          />
          <CreateEventDialog
            open={createEventDialogOpen}
            onClose={handleCreateEventDialogClosed}
          />
          <TabContent index={CALENDAR_VIEW_TAB_INDEX} value={activeTabIndex}>
            <CalendarControls
              selectedEventName={selectedEventName}
              setSelectedEventName={setSelectedEventName}
              selectedTimeSlice={selectedTimeSlice}
              setSelectedTimeSlice={setSelectedTimeSlice}
              currentDate={currentCalendarDate}
              setCurrentDate={setCurrentCalendarDate}
            />
          </TabContent>
          {isMobile ? null : (
            <TabSwitcher
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
            />
          )}
        </RowButColumnOnMobile>
        <TabContent value={activeTabIndex} index={LIST_VIEW_TAB_INDEX}>
          <SectionSeparator>
            <SavedFilterTabs {...savedViews} />
            <ConditionalLinearProgress
              visible={showTableLoading || isCalendarLoading}
            />
            <ScrollableTableContainer
              sx={{
                maxHeight: {
                  xs: "calc(100vh - 220px)",
                  sm: "calc(100vh - 470px)",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <StickyHeaderTableCell sx={stickyCheckboxCellSx}>
                      <Checkbox
                        size="small"
                        color="primary"
                        disabled={showTableLoading || tableRows.length === 0}
                        checked={
                          !showTableLoading &&
                          allSelectableChecked(checkedIDS, tableRows)
                        }
                        onChange={handleSelectAllCandidatesCheckboxCheckChanged}
                      />
                    </StickyHeaderTableCell>
                    <SortableHeaderCell
                      HeaderCellComponent={StickyHeaderTableCell}
                      columnName="candidate_name"
                      label="Candidate"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                      props={{ sx: stickyNameCellSx }}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="annual_value"
                      label="Annual Value"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <LikelihoodToMoveHeaderTableCell
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSortColumnChanged={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="custom_tag_label"
                      label="Tags"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="ui_source"
                      label="Type"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="rating"
                      label="Rating"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="stamp"
                      label="Appointment Date"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="company_name"
                      label="Candidate Company"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="phone"
                      label="Phone"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="email"
                      label="Email"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="crm_stage__name"
                      label="Stage"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <SortableHeaderCell
                      HeaderCellComponent={HeaderTableCell}
                      columnName="table_notes"
                      label="Notes"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClick={onSortColumnChanged}
                    />
                    <HeaderTableCell />
                    <HeaderTableCell
                      align="center"
                      sx={{ width: 48, px: 0.5 }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showTableLoading ? (
                    Array.from({ length: 10 }).map((_, index) => (
                      <TableRow key={index}>
                        <StickyTableCell sx={stickyCheckboxCellSx}>
                          <Skeleton variant="rounded" width={24} height={24} />
                        </StickyTableCell>
                        <StickyTableCell sx={stickyNameCellSx}>
                          <Skeleton variant="text" width={180} />
                        </StickyTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={80} height={24} />
                        </NoWrapTableCell>
                        <NumberTableCell>
                          <Skeleton variant="rounded" width={120} height={24} />
                        </NumberTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={100} height={36} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="text" width={100} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="text" width={120} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="text" width={100} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="text" width={80} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={60} height={24} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="text" width={150} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={120} height={36} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={130} height={36} />
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Skeleton variant="rounded" width={120} height={36} />
                        </NoWrapTableCell>
                        <NoWrapTableCell align="center">
                          <Skeleton variant="circular" width={28} height={28} />
                        </NoWrapTableCell>
                      </TableRow>
                    ))
                  ) : tableRows.length > 0 ? (
                    tableRows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          cursor: "pointer",
                          ...dncRowSx(row),
                        }}
                        onClick={handleRowClickedWrapper(row)}
                      >
                        <SelectMultipleTableCell
                          checkedIDs={checkedIDS}
                          setCheckedIDS={setCheckedIDS}
                          row={row}
                          rows={tableRows}
                          index={index}
                          disabled={isDncLead(row)}
                          disabledTooltip={DNC_SELECT_TOOLTIP}
                          CellComponent={StickyTableCell}
                          cellProps={{ sx: stickyCheckboxCellSx }}
                        />
                        <NameWithContactActionsCell
                          name={trimLongString(row.candidate_name)}
                          title={row.candidate_name}
                          phone={row.phone}
                          email={row.email}
                          isDnc={isDncLead(row)}
                          onCall={(e) =>
                            candidateActions.handleCallButtonClicked(e, row)
                          }
                          onSMS={(e) =>
                            candidateActions.handleSMSButtonClicked(e, row)
                          }
                          onEmail={(e) =>
                            candidateActions.handleEmailButtonClicked(e, row)
                          }
                        />
                        <NoWrapTableCell>
                          {formatCurrency(row.annual_value)}
                        </NoWrapTableCell>
                        <LikelihoodToMoveTableCell row={row} />
                        <NoWrapTableCell>
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            sx={{ maxWidth: 220, py: 0.25, gap: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {(row.custom_tags ?? []).length === 0 ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            ) : (
                              (row.custom_tags ?? []).map((tag, i) => (
                                <CandidatePriceTagChip
                                  key={`${row.id}-${i}-${tag}`}
                                  label={trimLongString(tag, 24)}
                                  title={tag}
                                  maxWidth={160}
                                />
                              ))
                            )}
                          </Stack>
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <GreenCircleChip
                            label={getCandidateTypeLabel(row)}
                            icon={
                              row.is_radar ? (
                                <GrayCircleIcon />
                              ) : row.type === RESULT_TYPES.booking ? (
                                <GreenCircleIcon />
                              ) : (
                                <PinkCircleIcon />
                              )
                            }
                          />
                        </NoWrapTableCell>
                        <NumberTableCell>
                          <RatingEditor
                            rating={row.rating}
                            setRating={handleRatingChangedWrapper(row)}
                          />
                        </NumberTableCell>
                        <NoWrapTableCell>
                          {row.stamp ? formatTableDate(row.stamp) : null}
                        </NoWrapTableCell>
                        <NoWrapTableCell title={row.company_name}>
                          {trimLongString(row.company_name)}
                        </NoWrapTableCell>
                        <NoWrapTableCell title={phoneDisplay(row.phone)}>
                          {trimLongString(phoneDisplay(row.phone))}
                        </NoWrapTableCell>
                        <NoWrapTableCell title={row.email}>
                          {trimLongString(row.email)}
                        </NoWrapTableCell>
                        <NoWrapTableCell title={row.crm_stage}>
                          <BorderedGrayChip
                            label={trimLongString(row.crm_stage)}
                          />
                        </NoWrapTableCell>
                        <NoWrapTableCell title={row.table_notes}>
                          {row.table_notes
                            ? trimLongString(row.table_notes)
                            : ""}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          <Button
                            variant="outlined"
                            color="primary"
                            sx={{ width: "10.5em" }}
                            onClick={(e) =>
                              openUpdateCreateCandidateFeedbackDialog(e, row)
                            }
                          >
                            {row.feedback ? "Update" : "Send"} feedback
                          </Button>
                        </NoWrapTableCell>
                        <NoWrapTableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Remove from portal">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Remove candidate from portal"
                                disabled={
                                  isArchivingCandidate || isArchivingCandidates
                                }
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setCandidateToArchive(row)
                                }}
                              >
                                <DeleteOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </NoWrapTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoResultsTableRow colSpan={15} />
                  )}
                </TableBody>
                {candidatePartialDetailsMobileDialogOpen ? (
                  <CandidatePartialDetailsMobileDialog
                    open={!!candidatePartialDetailsMobileDialogOpen}
                    onClose={() => setCandidatePartialDetailsMobileOpen(null)}
                    candidate={
                      candidatePartialDetailsMobileDialogOpen.candidate
                    }
                    listNavigationState={
                      candidatePartialDetailsMobileDialogOpen.listNavigationState
                    }
                  />
                ) : null}
              </Table>
              <UpdateCreateCandidateFeedbackDialog
                open={updateCreateCandidateFeedbackDialogOpen}
                candidate={candidateReceivingFeedback}
                onClose={closeUpdateCreateCandidateFeedbackDialog}
              />
              <ConfirmArchiveCandidateDialog
                open={!!candidateToArchive}
                candidateName={candidateToArchive?.candidate_name}
                onClose={handleConfirmArchiveCandidateFromList}
              />
              <ConfirmArchiveCandidatesDialog
                open={confirmArchiveCandidatesDialogOpen}
                candidateCount={checkedIDS.size}
                onClose={handleConfirmArchiveCandidatesDialogClosed}
              />
            </ScrollableTableContainer>
            <TablePaginationRow
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              tableRows={tableRows}
              totalResultsSize={totalResultsSize}
            />
          </SectionSeparator>
        </TabContent>
        <TabContent value={activeTabIndex} index={CALENDAR_VIEW_TAB_INDEX}>
          <CandidatesCalendar
            onIsLoadingChanged={setIsCalendarLoading}
            currentDate={currentCalendarDate}
            selectedTimeSlice={selectedTimeSlice}
            selectedEventType={selectedEventName}
          />
        </TabContent>
      </PageContentContainer>
      {/* Per-row action dialogs */}
      <CandidateActionDialogs
        actionsState={candidateActions}
        openSnackbar={openSnackbar}
      />
      <CloseableSnackbar
        open={isSnackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={closeSnackbar}
      />
    </Column>
  )
}

function TabSwitcher({ activeTabIndex, setActiveTabIndex }) {
  const TAB_DATA = [
    new TabData("List", SegmentOutlinedIcon),
    new TabData("Calendar", CalendarTodayOutlinedIcon),
  ]
  const isMobile = useIsMobile()
  return (
    <ButtonGroup variant="outlined" color="inherit" size="small">
      {TAB_DATA.map((tabData, i) => (
        <Button
          startIcon={<tabData.icon />}
          className={i === activeTabIndex ? "active" : undefined}
          onClick={() => setActiveTabIndex(i)}
          sx={{
            width: { xs: "50px", sm: i === 0 ? "105px" : "128px" },
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
          }}
          key={i}
        >
          {isMobile ? "" : tabData.title}
        </Button>
      ))}
    </ButtonGroup>
  )
}

const RESULT_TYPES = {
  booking: "booking",
  marketing: "marketing",
}
